import { useQuery } from '@tanstack/react-query';
import { BookOpenCheck, Check, Download, Flag, Share2, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { Janela, Sobreposicao } from '@/componentes/JanelaDeAcao';
import { pedir } from '@/lib/pedido';
import { anunciar } from '@/lib/retorno';

import {
  CATALOGO_DE_HISTORIAS,
  estadoDoMarcador,
  fraseDaHistoria,
  ROTULO_DA_SITUACAO,
  ROTULO_DO_TIPO,
  tipoDoCodigo,
  type EstadoDoMarcador,
  type TipoDaHistoria,
} from './catalogo';
import { contagemDaTela, useModoHistorias } from './contexto';
import { linkDaHistoria, roteiroDeTeste } from './roteiro';

/**
 * O MODO HISTÓRIAS NA MESA (manual v2.1, p.46 a p.49).
 *
 * - p.46: o interruptor "Homologação · Histórias" na barra superior; ligado,
 *   cada elemento com história ganha um marcador numerado, e a barra diz
 *   "9 na tela · 3 conferidas" com o botão "Lista".
 * - p.47: o marcador abre o cartão COLADO a ele, sem bloquear a tela, com as
 *   dez partes em ordem; Esc fecha.
 * - p.48: a lista da tela, os filtros, o roteiro de teste e a divergência com
 *   "Enviar para". Nada disso aparece na impressão.
 * - p.49: no código, o elemento recebe `data-historia="IND-PNL-01"`.
 *
 * O estado (ligado, conferências) é o mesmo do celular: `ProvedorDeHistorias`.
 */

const MARCADOR: Readonly<Record<EstadoDoMarcador, string>> = {
  padrao: 'bg-marca text-branco',
  conferida: 'bg-operando text-branco',
  divergente: 'bg-parada text-branco',
  sem_historia: 'border-2 border-dashed border-tinta-apagada bg-card text-tinta-secundaria',
};

const NOME_DO_ESTADO: Readonly<Record<EstadoDoMarcador, string>> = {
  padrao: 'ainda não conferida',
  conferida: 'conferida',
  divergente: 'divergente',
  sem_historia: 'sem história escrita',
};

/**
 * O elemento com história. Desligado, não desenha nada além do filho: a tela
 * fica idêntica à de produção. O elemento continua fazendo o que faria — o
 * marcador é um botão próprio, no canto.
 */
export function HistoriaNaMesa({
  codigo,
  children,
  className = '',
}: {
  readonly codigo: string;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const modo = useModoHistorias();
  const { registrar, ativo } = modo;
  useEffect(() => (ativo ? registrar(codigo) : undefined), [ativo, registrar, codigo]);
  const estado = estadoDoMarcador(codigo, modo.conferencias.get(codigo));
  const ordem = modo.naTela.indexOf(codigo) + 1;
  const aberto = modo.aberta === codigo;
  return (
    <div className={`relative ${className}`} data-historia={codigo}>
      {children}
      {ativo ? (
        <button
          type="button"
          data-historia-marcador={codigo}
          aria-expanded={aberto}
          aria-label={`História ${codigo}, ${NOME_DO_ESTADO[estado]}`}
          onClick={() => {
            if (aberto) modo.fechar();
            else modo.abrir(codigo);
          }}
          className={`txt-legenda absolute -top-2 -right-2 z-[var(--z-historias)] inline-flex size-6 items-center justify-center rounded-pill font-semibold shadow-sobreposicao print:hidden ${MARCADOR[estado]} ${aberto ? 'ring-2 ring-marca ring-offset-2' : ''}`}
        >
          {estado === 'sem_historia' ? '?' : String(ordem || '•')}
        </button>
      ) : null}
    </div>
  );
}

/** "Homologação · Histórias" na barra superior (p.46, p.48). Só em homologação. */
export function InterruptorDeHistorias() {
  const modo = useModoHistorias();
  const [lista, setLista] = useState(false);
  if (!modo.homologacao) return null;
  const c = contagemDaTela(modo.naTela, modo.conferencias);
  return (
    <div className="flex shrink-0 items-center gap-2 print:hidden">
      <button
        type="button"
        role="switch"
        aria-checked={modo.ligadoNoAparelho}
        onClick={modo.alternar}
        title="Atalho: Ctrl ⇧ H"
        className="txt-apoio inline-flex h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-pill border border-borda bg-card px-3 text-tinta"
      >
        {/* A chave da p.21: o pino desliza e o trilho troca de cor ao mesmo tempo (200 ms, padrão). */}
        <span
          aria-hidden="true"
          className={`inline-flex h-4 w-7 items-center rounded-pill p-0.5 transition-colors duration-[var(--mov-padrao)] ease-[var(--mov-padrao-curva)] motion-reduce:transition-none ${modo.ligadoNoAparelho ? 'bg-marca' : 'bg-borda'}`}
        >
          <span
            className={`size-3 rounded-pill bg-branco transition-transform duration-[var(--mov-padrao)] ease-[var(--mov-padrao-curva)] motion-reduce:transition-none ${modo.ligadoNoAparelho ? 'translate-x-3' : 'translate-x-0'}`}
          />
        </span>
        Homologação · Histórias
      </button>
      {modo.ativo ? (
        <>
          <span className="txt-apoio whitespace-nowrap text-tinta-secundaria tabular-nums max-[1536px]:hidden">
            {c.total} na tela · {c.conferidas} {c.conferidas === 1 ? 'conferida' : 'conferidas'}
          </span>
          <Botao
            nivel="fantasma"
            tamanho="p"
            onClick={() => {
              setLista(true);
            }}
          >
            Lista
          </Botao>
          <ListaDaTela
            aberta={lista}
            aoFechar={() => {
              setLista(false);
            }}
          />
        </>
      ) : null}
    </div>
  );
}

type Filtro = 'todos' | 'indicadores' | 'divergentes' | 'sem_historia';

/** Em quê (R04, p.32): o código do elemento e, se o catálogo tem, o título da história. */
function alvoDaHistoria(codigo: string): { readonly codigo: string; readonly nome?: string } {
  const titulo = CATALOGO_DE_HISTORIAS.get(codigo)?.titulo;
  return titulo ? { codigo, nome: titulo } : { codigo };
}

/** A lista "Histórias desta tela" (p.48): filtros, numeração igual à dos marcadores e o roteiro de teste. */
function ListaDaTela({ aberta, aoFechar }: { readonly aberta: boolean; readonly aoFechar: () => void }) {
  const modo = useModoHistorias();
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const itens = modo.naTela
    .map((codigo, i) => ({ codigo, ordem: i + 1, estado: estadoDoMarcador(codigo, modo.conferencias.get(codigo)) }))
    .filter((x) =>
      filtro === 'todos'
        ? true
        : filtro === 'indicadores'
          ? tipoDoCodigo(x.codigo) === 'IND'
          : filtro === 'divergentes'
            ? x.estado === 'divergente'
            : x.estado === 'sem_historia',
    );
  const exportar = () => {
    const agora = new Date();
    const quando = `${agora.toLocaleDateString('pt-BR')} · ${agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    const texto = roteiroDeTeste(`${window.location.pathname}${window.location.search}`, modo.naTela, modo.conferencias, quando);
    const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `roteiro-de-teste${window.location.pathname.replace(/\//g, '-') || '-inicio'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    anunciar({
      tom: 'sucesso',
      titulo: 'Roteiro de teste exportado',
      alvo: { nome: window.location.pathname },
      fatos: [`${String(modo.naTela.length)} ${modo.naTela.length === 1 ? 'elemento' : 'elementos'}`, a.download],
    });
  };
  return (
    <Sobreposicao lugar="direita" titulo="Histórias desta tela" descricao="A numeração é a dos marcadores. Ctrl ⇧ H liga e desliga." aberta={aberta} aoFechar={aoFechar}>
      <div className="flex flex-col gap-4 print:hidden">
        <div role="group" aria-label="Filtrar histórias" className="flex flex-wrap gap-2">
          {(
            [
              ['todos', 'Todos'],
              ['indicadores', 'Indicadores'],
              ['divergentes', 'Divergentes'],
              ['sem_historia', 'Sem história'],
            ] as const
          ).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              aria-pressed={filtro === valor}
              onClick={() => {
                setFiltro(valor);
              }}
              className={`txt-apoio inline-flex h-8 items-center rounded-pill border px-3 ${filtro === valor ? 'border-marca bg-marca-fundo text-marca-texto' : 'border-borda bg-card text-tinta'}`}
            >
              {rotulo}
            </button>
          ))}
        </div>
        {itens.length === 0 ? (
          <p className="txt-corpo text-tinta-secundaria">
            {modo.naTela.length === 0 ? 'Nenhum elemento desta tela tem marcador ainda.' : 'Nenhum elemento com esse filtro. Escolha Todos para ver a tela inteira.'}
          </p>
        ) : (
          <ol className="flex flex-col divide-y divide-borda-sutil rounded-card border border-borda-sutil bg-card">
            {itens.map((x) => {
              const h = CATALOGO_DE_HISTORIAS.get(x.codigo);
              const tipo = tipoDoCodigo(x.codigo);
              return (
                <li key={x.codigo} className="flex items-start gap-3 px-4 py-3">
                  <span aria-hidden="true" className={`txt-legenda mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-pill font-semibold ${MARCADOR[x.estado]}`}>
                    {x.estado === 'sem_historia' ? '?' : String(x.ordem)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="txt-corpo text-tinta">{h?.titulo ?? 'Elemento sem história'}</p>
                    <p className="txt-apoio text-tinta-secundaria">
                      <span className="txt-codigo">{x.codigo}</span> · {tipo ? ROTULO_DO_TIPO[tipo] : 'Elemento'} · {NOME_DO_ESTADO[x.estado]}
                    </p>
                  </div>
                  <Botao
                    nivel="fantasma"
                    tamanho="p"
                    onClick={() => {
                      aoFechar();
                      if (h) modo.abrir(x.codigo);
                      else void escreverHistoria(x.codigo);
                    }}
                  >
                    {h ? 'Abrir' : 'Escrever'}
                  </Botao>
                </li>
              );
            })}
          </ol>
        )}
        <div>
          <Botao nivel="secundario" onClick={exportar} disabled={modo.naTela.length === 0}>
            <Download aria-hidden="true" className="size-[var(--icone-controle)]" />
            Exportar roteiro de teste
          </Botao>
        </div>
      </div>
    </Sobreposicao>
  );
}

/**
 * "Escrever" (p.48): o catálogo mora no código, versionado com a tela, então
 * a história se escreve lá. O botão copia o esqueleto com o código e os campos
 * do cartão, para quem escreve não esquecer nenhum.
 */
async function escreverHistoria(codigo: string): Promise<void> {
  const esqueleto = [
    `{`,
    `  codigo: '${codigo}', titulo: '', papel: '',`,
    `  quero: '', para: '',`,
    `  significa: '',`,
    `  calcula: '',`,
    `  origem: '',`,
    `  criterios: ['', ''],`,
    `  situacao: 'rascunho', responsavel: '', revisadaEm: REVISAO,`,
    `},`,
  ].join('\n');
  try {
    await navigator.clipboard.writeText(esqueleto);
    anunciar({ tom: 'sucesso', titulo: 'Esqueleto da história copiado', alvo: { codigo }, efeito: 'Cole no catálogo, em src/historias' });
  } catch {
    anunciar({ tom: 'falha', titulo: 'Esqueleto não copiado', alvo: { codigo }, comoResolver: `Escreva a história ${codigo} no catálogo, em src/historias, no formato da p.49.` });
  }
}

/** O cartão da história, colado ao marcador (p.47). Montado uma vez, na casca. */
export function CartaoDaHistoriaNaMesa() {
  const modo = useModoHistorias();
  const codigo = modo.aberta;
  const caixa = useRef<HTMLDivElement>(null);
  const [lugar, setLugar] = useState<{ top: number; left: number } | null>(null);
  const [divergindo, setDivergindo] = useState(false);
  const h = codigo ? CATALOGO_DE_HISTORIAS.get(codigo) : undefined;
  const conferencia = codigo ? modo.conferencias.get(codigo) : undefined;
  const tipo = codigo ? tipoDoCodigo(codigo) : null;

  /* Colado ao marcador, a 8 px, virando para o outro lado se não couber (p.31). */
  useLayoutEffect(() => {
    if (!codigo) {
      setLugar(null);
      return undefined;
    }
    const posicionar = () => {
      const marcador = document.querySelector(`[data-historia-marcador="${codigo}"]`);
      const largura = caixa.current?.offsetWidth ?? 400;
      const altura = caixa.current?.offsetHeight ?? 480;
      if (!marcador) {
        setLugar({ top: 96, left: Math.max(16, window.innerWidth - largura - 24) });
        return;
      }
      const r = marcador.getBoundingClientRect();
      let left = r.right + 8;
      if (left + largura > window.innerWidth - 16) left = Math.max(16, r.left - largura - 8);
      let top = r.top;
      if (top + altura > window.innerHeight - 16) top = Math.max(16, window.innerHeight - altura - 16);
      setLugar({ top, left });
    };
    posicionar();
    window.addEventListener('resize', posicionar);
    window.addEventListener('scroll', posicionar, true);
    return () => {
      window.removeEventListener('resize', posicionar);
      window.removeEventListener('scroll', posicionar, true);
    };
  }, [codigo]);

  useEffect(() => {
    if (!codigo || divergindo) return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') modo.fechar();
    };
    window.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('keydown', tecla);
    };
  }, [codigo, divergindo, modo]);

  if (!codigo) return null;

  const copiarLink = () => {
    const link = linkDaHistoria(window.location.origin, window.location.pathname, window.location.search, codigo);
    navigator.clipboard.writeText(link).then(
      () => {
        anunciar({ tom: 'sucesso', titulo: 'Link da história copiado', alvo: alvoDaHistoria(codigo) });
      },
      () => {
        anunciar({ tom: 'falha', titulo: 'Link da história não copiado', alvo: alvoDaHistoria(codigo), comoResolver: `Copie o endereço da tela e acrescente #historia=${codigo}.` });
      },
    );
  };
  const marcarConferida = () => {
    modo.conferir(codigo, 'conferida').then(
      () => {
        anunciar({ tom: 'sucesso', titulo: 'História conferida', alvo: alvoDaHistoria(codigo) });
        modo.fechar();
      },
      (erro: unknown) => {
        anunciar({
          tom: 'falha',
          titulo: 'Conferência não gravada',
          alvo: alvoDaHistoria(codigo),
          ...(erro instanceof Error && erro.message ? { fatos: [erro.message] } : {}),
          comoResolver: 'Confira a conexão e tente de novo.',
        });
      },
    );
  };

  return (
    <>
      <div
        ref={caixa}
        role="dialog"
        aria-label={`História ${codigo}`}
        style={lugar ? { top: lugar.top, left: lugar.left } : { visibility: 'hidden' }}
        className="fixed z-[var(--z-historias)] flex max-h-[calc(100vh-var(--esp-8))] w-[400px] max-w-[calc(100vw-var(--esp-8))] flex-col gap-3 overflow-auto rounded-card border border-borda-sutil bg-card p-4 shadow-sobreposicao print:hidden"
      >
        <div className="flex items-start gap-2">
          <p className="txt-apoio flex-1 text-tinta-secundaria">
            {tipo ? ROTULO_DO_TIPO[tipo as TipoDaHistoria] : 'Elemento'} · <span className="txt-codigo">{codigo}</span>
          </p>
          <span className={`txt-selo inline-flex h-6 items-center gap-1.5 rounded-pill px-2 ${conferencia?.situacao === 'divergente' ? 'bg-erro-fundo text-erro-texto' : conferencia?.situacao === 'conferida' ? 'bg-operando-fundo text-operando-texto' : 'bg-rebaixada text-tinta-secundaria'}`}>
            {conferencia ? (conferencia.situacao === 'conferida' ? 'Conferida' : 'Divergente') : h ? ROTULO_DA_SITUACAO[h.situacao] : 'Sem história'}
          </span>
          <button type="button" aria-label="Fechar a história" onClick={modo.fechar} className="inline-flex size-6 items-center justify-center rounded-pill text-tinta-secundaria">
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>
        {!h ? (
          <p className="txt-corpo text-tinta-secundaria">Este elemento ainda não tem história. Ela entra no catálogo com o código {codigo}, no formato da p.49 do manual.</p>
        ) : (
          <>
            <h2 className="txt-subtitulo text-tinta">{h.titulo}</h2>
            <p className="txt-corpo text-tinta">{fraseDaHistoria(h)}</p>
            <Parte rotulo="O que significa">{h.significa}</Parte>
            <Parte rotulo="Como se calcula">{h.calcula}</Parte>
            <Parte rotulo="De onde vem">{h.origem}</Parte>
            <div>
              <p className="txt-rotulo-campo">Critérios de aceite</p>
              <ul className="mt-1 flex flex-col gap-1">
                {h.criterios.map((c) => (
                  <li key={c} className="txt-apoio flex gap-2 text-tinta-secundaria">
                    <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-tinta-apagada" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            {conferencia?.situacao === 'divergente' && conferencia.nota ? (
              <p className="txt-apoio rounded-controle bg-erro-fundo px-3 py-2 text-erro-texto">Divergência: {conferencia.nota}</p>
            ) : null}
            <p className="txt-legenda text-tinta-apagada">
              {h.responsavel} · revisada em {h.revisadaEm}
              {conferencia ? ` · última conferência por ${conferencia.quem ?? 'alguém'}` : ''}
            </p>
            <div className="flex flex-wrap items-center gap-2 border-t border-borda-sutil pt-3">
              <Botao nivel="fantasma" tamanho="p" onClick={copiarLink}>
                <Share2 aria-hidden="true" className="size-[var(--icone-controle)]" />
                Copiar link
              </Botao>
              <Botao
                nivel="fantasma"
                tamanho="p"
                onClick={() => {
                  setDivergindo(true);
                }}
              >
                <Flag aria-hidden="true" className="size-[var(--icone-controle)]" />
                Apontar divergência
              </Botao>
              <span className="flex-1" />
              <Botao nivel="secundario" tamanho="p" disabled={modo.conferindo} onClick={marcarConferida}>
                <BookOpenCheck aria-hidden="true" className="size-[var(--icone-controle)]" />
                Marcar conferida
              </Botao>
            </div>
          </>
        )}
      </div>
      <JanelaDeDivergencia
        codigo={codigo}
        aberta={divergindo}
        aoFechar={() => {
          setDivergindo(false);
        }}
      />
    </>
  );
}

function Parte({ rotulo, children }: { readonly rotulo: string; readonly children: ReactNode }) {
  return (
    <div>
      <p className="txt-rotulo-campo">{rotulo}</p>
      <p className="txt-apoio mt-1 text-tinta-secundaria">{children}</p>
    </div>
  );
}

/** "Apontar divergência" (p.48): o que está diferente, para quem vai, e o anexo da tela. */
function JanelaDeDivergencia({ codigo, aberta, aoFechar }: { readonly codigo: string; readonly aberta: boolean; readonly aoFechar: () => void }) {
  const modo = useModoHistorias();
  const base = useId();
  const [nota, setNota] = useState('');
  const [destino, setDestino] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const destinos = useQuery({
    queryKey: ['historias', 'destinos'],
    enabled: aberta,
    staleTime: 5 * 60_000,
    queryFn: () => pedir<{ destinos: readonly { id: string; nome: string }[] }>('/historias/destinos'),
  });
  const enviar = () => {
    if (nota.trim().length < 10) {
      setErro('Diga o que está diferente, com pelo menos 10 letras — por exemplo, "o indicador mostra 4 e a lista tem 3".');
      return;
    }
    modo.conferir(codigo, 'divergente', nota.trim(), destino || undefined).then(
      (para) => {
        anunciar({
          tom: 'sucesso',
          titulo: 'Divergência enviada',
          alvo: alvoDaHistoria(codigo),
          fatos: [`para ${para ?? 'quem administra a governança'}`],
        });
        setNota('');
        setDestino('');
        aoFechar();
        modo.fechar();
      },
      (falha: unknown) => {
        anunciar({
          tom: 'falha',
          titulo: 'Divergência não enviada',
          alvo: alvoDaHistoria(codigo),
          ...(falha instanceof Error && falha.message ? { fatos: [falha.message] } : {}),
          comoResolver: 'Confira a conexão e tente de novo.',
        });
      },
    );
  };
  return (
    <Janela
      titulo="Apontar divergência"
      descricao={`A história ${codigo} não bate com a tela. O marcador fica vermelho até alguém conferir de novo.`}
      aberta={aberta}
      largura="m"
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="secundario" onClick={aoFechar}>
            Manter a história
          </Botao>
          <Botao nivel="primario" disabled={modo.conferindo} onClick={enviar}>
            Enviar divergência
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo id={`${base}-nota`} rotulo="O que está diferente" erro={erro} ajuda="Pelo menos 10 letras. A tela, o filtro e o endereço vão junto.">
          <textarea
            id={`${base}-nota`}
            rows={4}
            maxLength={1000}
            className="controle h-auto w-full py-2"
            placeholder="O indicador mostra 4, e a lista aberta por ele tem 3"
            value={nota}
            onChange={(e) => {
              setNota(e.target.value);
              setErro(null);
            }}
          />
        </Campo>
        <Campo id={`${base}-para`} rotulo="Enviar para" opcional ajuda="Sem escolha, vai para quem administra a governança.">
          <select
            id={`${base}-para`}
            className="controle w-full"
            value={destino}
            onChange={(e) => {
              setDestino(e.target.value);
            }}
          >
            <option value="">Quem administra a governança</option>
            {(destinos.data?.destinos ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.nome}
              </option>
            ))}
          </select>
        </Campo>
      </div>
    </Janela>
  );
}
